/**
 * One-line "how to lodge" summary for a submission requirement.
 *
 * Shared by the Submission Pack rows and the Analysis checklist so the two
 * surfaces describe the same item in the same words. Only the parts the tender
 * actually stated are shown — an absent part is omitted, never blanked.
 */

export interface LodgementDetailsInput {
  method: string | null | undefined
  format: string | null | undefined
  limit: string | null | undefined
  /** The server stringifies an INTEGER column, so both shapes arrive. */
  copies: string | number | null | undefined
}

const SEPARATOR = ' · '

function clean(value: string | null | undefined): string | null {
  if (value == null) return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/** `1` → "1 copy", any other count → "N copies". Free text passes through. */
function formatCopies(copies: string | number | null | undefined): string | null {
  if (copies == null) return null
  const raw = typeof copies === 'number' ? String(copies) : copies.trim()
  if (raw === '') return null
  if (!/^\d+$/.test(raw)) return raw
  return Number(raw) === 1 ? '1 copy' : `${Number(raw)} copies`
}

/**
 * `method · format · limit · N copies`, in that order, skipping absent parts.
 * Returns an empty string when the tender stated none of them.
 */
export function formatLodgementDetails(item: LodgementDetailsInput): string {
  return [clean(item.method), clean(item.format), clean(item.limit), formatCopies(item.copies)]
    .filter((part): part is string => part !== null)
    .join(SEPARATOR)
}
